<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { useUserStore } from '@/store/user'
import { useRoute, useRouter } from 'vue-router'
import { useWindowSize } from '@vueuse/core'
import AppSidebar from './AppSidebar.vue'
import AppHeader from './AppHeader.vue'
import AppFooter from './AppFooter.vue'
import FloatingChat from '@/components/FloatingChat.vue'
import PageSkeleton from '@/components/PageSkeleton.vue'

const router = useRouter()
const route = useRoute()
const userStore = useUserStore()

// 侧边栏宽度控制
const isCollapsed = ref(false)
const mobileNavigationOpen = ref(false)
const sidebarWidth = computed(() => isCollapsed.value ? 64 : 224)

// 客户端宽度
const { width: clientWidth } = useWindowSize()

// 是否是移动端
const isMobile = computed(() => clientWidth.value < 768)

// 用户信息
const userInfo = computed(() => userStore.userInfo)

// 监听移动端状态变化
watch(isMobile, (val) => {
  userStore.setMobileType(val)
  mobileNavigationOpen.value = false
}, { immediate: true })

watch(() => clientWidth.value < 1024, (compact) => {
  isCollapsed.value = compact
}, { immediate: true })

watch(() => route.fullPath, () => { mobileNavigationOpen.value = false })

function toggleNavigation() {
  if (isMobile.value) mobileNavigationOpen.value = !mobileNavigationOpen.value
  else isCollapsed.value = !isCollapsed.value
}

onMounted(() => {
  // 如果未登录且当前页面需要认证，跳转登录页
  const requiresAuth = router.currentRoute.value.meta?.requiresAuth !== false
  if (!userInfo.value && requiresAuth) {
    router.push('/login')
  }
})
</script>

<template>
  <div class="main-layout">
    <a-layout>
      <!-- 占位 Sider - 用于保持内容区域位置 -->
      <div
        v-if="!isMobile"
        class="sider-placeholder"
        :style="{
          width: `${sidebarWidth}px`,
          flex: `0 0 ${sidebarWidth}px`,
          maxWidth: `${sidebarWidth}px`,
          minWidth: `${sidebarWidth}px`,
        }"
      />

      <!-- 固定侧边栏 -->
      <a-layout-sider
        v-if="!isMobile"
        v-model:collapsed="isCollapsed"
        :collapsed-width="64"
        :width="224"
        :trigger="null"
        class="fixed-sidebar"
      >
        <AppSidebar :collapsed="isCollapsed" />
      </a-layout-sider>

      <a-drawer
        v-else
        v-model:open="mobileNavigationOpen"
        placement="left"
        title="工作台导航"
        :width="280"
        :body-style="{ padding: 0 }"
      >
        <AppSidebar :collapsed="false" />
      </a-drawer>

      <!-- 主内容区域 -->
      <a-layout class="main-content-layout">
        <!-- 顶部栏 -->
        <a-layout-header class="layout-header">
          <AppHeader
            :navigation-expanded="isMobile ? mobileNavigationOpen : !isCollapsed"
            @toggle-navigation="toggleNavigation"
          />
        </a-layout-header>

        <!-- 内容区 -->
        <a-layout-content id="workspace-content" class="layout-content" role="main">
        <router-view v-slot="{ Component }">
          <Suspense>
            <template #default>
              <component :is="Component" :key="route.fullPath" />
            </template>
            <template #fallback>
              <PageSkeleton />
            </template>
          </Suspense>
        </router-view>
        </a-layout-content>

        <!-- 页脚 -->
        <a-layout-footer class="layout-footer">
          <AppFooter />
        </a-layout-footer>
      </a-layout>
    </a-layout>

    <!-- 浮动聊天组件 -->
    <FloatingChat />
  </div>
</template>

<style scoped lang="scss">
.main-layout {
  width: 100%;
  min-height: 100vh;
  background: var(--bg-color);
}

.sider-placeholder {
  overflow: hidden;
  transition: width 0.2s, flex-basis 0.2s, min-width 0.2s, max-width 0.2s;
}

.fixed-sidebar {
  height: 100vh;
  height: 100dvh;
  z-index: 99;
  position: fixed;
  left: 0;
  overflow: auto;
  background: var(--card-bg);
  border-right: 1px solid var(--border-color);

  :deep(.ant-layout-sider-children) {
    display: flex;
    flex-direction: column;
  }
}

.main-content-layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  min-width: 0;
  background: var(--bg-color);
}

.layout-header {
  padding: 0;
  height: 56px;
  line-height: normal;
  background: var(--card-bg);
  border-bottom: 1px solid var(--border-color);
  flex-shrink: 0;
}

.layout-content {
  flex: 1;
  min-width: 0;
}


.layout-footer {
  padding: 0;
  background: transparent;
  flex-shrink: 0;
}

@media (prefers-reduced-motion: reduce) {
  .sider-placeholder { transition: none; }
}
</style>
